import { Download } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { Campo } from '@/components/Campo';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { guardarArchivo } from '@/lib/descargar';
import { fechaHora, numero } from '@/lib/formato';

import { FiltrosFechas } from './OpinionesPage';
import { useEventos, type FiltrosEventos } from './queries';

const CLAVES = ['from', 'to', 'type', 'userId', 'q'] as const;

const TIPOS_EVENTO: Record<string, string> = {
  print_screen: 'Captura de pantalla',
  focus_lost_viewer: 'Salió del visor',
  ios_screenshot: 'Captura en iOS',
  ios_recording: 'Grabación en iOS',
  android_capture_blocked: 'Captura bloqueada (Android)',
  rate_limit: 'Demasiadas solicitudes',
  devtools: 'Herramientas de desarrollo',
  print_attempt: 'Intento de imprimir',
  context_menu: 'Menú contextual',
  copy_attempt: 'Intento de copiar',
};

function Seguridad() {
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const q = useEventos({ ...(query as FiltrosEventos), page: filtros.page, pageSize: 50 });
  const { proteger, dialogo } = useStepUp();
  const [exportando, setExportando] = useState(false);
  const exportar = async () => {
    setExportando(true);
    try {
      await proteger(async () =>
        guardarArchivo(
          await api.GET('/api/v1/admin/security-events/export', {
            params: { query: query },
            parseAs: 'blob',
          }),
          'eventos-seguridad.xlsx',
        ),
      );
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setExportando(false);
    }
  };
  const d = q.data?.data;
  return (
    <>
      <Encabezado
        titulo="Protección de contenido"
        descripcion="Lo que detectan la plataforma y las apps: capturas, grabación, intentos de copiar o imprimir."
        acciones={
          <button
            type="button"
            className="btn-cs btn-contorno"
            disabled={exportando}
            onClick={() => void exportar()}
          >
            <Download size={16} aria-hidden /> Exportar .xlsx
          </button>
        }
      />
      <div className="panel-vidrio mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <FiltrosFechas filtros={filtros} cambiar={cambiar} />
        <Campo etiqueta="Tipo">
          <select
            className="input-vidrio"
            value={filtros.type}
            onChange={(e) => cambiar({ type: e.target.value })}
          >
            <option value="">Todos</option>
            {Object.entries(TIPOS_EVENTO).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </select>
        </Campo>
      </div>
      {filtros.userId && (
        <p className="mb-3 text-sm">
          Solo el alumno #{filtros.userId}.{' '}
          <button type="button" className="underline" onClick={() => cambiar({ userId: '' })}>
            Quitar
          </button>
        </p>
      )}
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {d && (
        <div className="grid gap-6 xl:grid-cols-[1fr_20rem]">
          <div>
            {d.items.length === 0 ? (
              <Vacio titulo="Sin eventos con esos filtros" />
            ) : (
              <div className="panel-vidrio overflow-x-auto p-0">
                <table className="tabla-cs w-full text-sm">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Alumno</th>
                      <th>Evento</th>
                      <th>Contenido</th>
                      <th>Cliente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((e) => (
                      <tr key={e.id}>
                        <td className="whitespace-nowrap">
                          {e.occurredAt && fechaHora(e.occurredAt)}
                        </td>
                        <td>
                          <Link
                            to={`/alumnos/${e.userId}`}
                            className="font-semibold hover:underline"
                          >
                            {e.userName ?? `#${e.userId}`}
                          </Link>
                          <span className="block text-xs text-texto-3">{e.userEmail}</span>
                        </td>
                        <td>{TIPOS_EVENTO[e.type] ?? e.type}</td>
                        <td className="text-xs">
                          {e.contentType ?? '—'}
                          {e.contentId && ` #${e.contentId}`}
                        </td>
                        <td className="text-xs">{e.client ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {q.data && (
              <Paginador
                page={q.data.page}
                pageSize={q.data.pageSize}
                total={q.data.total}
                onPage={(page) => cambiar({ page })}
              />
            )}
          </div>
          <aside className="panel-vidrio h-fit">
            <h2 className="mb-2 font-extrabold">Alumnos con más eventos</h2>
            {d.topUsers.length === 0 && (
              <p className="text-sm text-texto-3">Nadie en este filtro.</p>
            )}
            <ol className="space-y-1 text-sm">
              {d.topUsers.map((u) => (
                <li key={u.userId} className="flex items-center gap-2">
                  <button
                    type="button"
                    className="flex-1 truncate text-left font-semibold hover:underline"
                    onClick={() => cambiar({ userId: String(u.userId) })}
                  >
                    {u.userName ?? `#${u.userId}`}
                  </button>
                  <span className="tabular">{numero(u.events)}</span>
                </li>
              ))}
            </ol>
          </aside>
        </div>
      )}
      {dialogo}
    </>
  );
}

export function SeguridadPage() {
  return (
    <PermissionGate seccion="reportes">
      <Seguridad />
    </PermissionGate>
  );
}
